import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap, throwError } from 'rxjs';
import { AppError } from '@domain-utils/app-error';
import { AppErrorItem } from '@domain-utils/app-error-item';
import { FileDownloadService } from './file-download.service';
import { FileUploadService } from './file-upload.service';
import { SERVICE_ERROR_CONSTANTS } from './constants/service-error.const';
import { UPLOAD_CONSTANTS } from './constants/upload.const';
import { APP_CONFIG, AppConfig } from '@cfg/env/app.config';
import { AppHttpClientApi } from '@helpers/api/http-client-api';
import { ApiException } from '@helpers/errors/api-error';
import {
  LoadRequest,
  UploadFileResponse,
  UploadIssueDetail,
  UploadResult
} from './models/load.model';

export type AppUploadError = AppError & { readonly generatedLoadId: string };

interface PolicyNumberResponse {
  policyNumber?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class BulkUploadsService {
  private readonly fileUploadService = inject(FileUploadService);
  private readonly fileDownloadService = inject(FileDownloadService);
  private readonly http = inject(AppHttpClientApi);
  private readonly appConfig: AppConfig = inject(APP_CONFIG);
  private readonly generatePolicyResource = 'cargamasivaemp/policy/generate';

  generatePolicyNumber(operationType: string): Observable<string> {
    if (this.appConfig.useMocks) {
      return of('012000136260');
    }

    return this.http
      .postPlain<PolicyNumberResponse>(this.generatePolicyResource, {
        operationType
      })
      .pipe(
        map((response) => this.resolvePolicyNumber(response)),
        catchError((error: unknown) => {
          if (error instanceof ApiException) {
            const codes: AppErrorItem[] = error.error.map((item) => ({
              key: item.key,
              message: item.message
            }));

            return throwError(
              () => new AppError('POLICY_NUMBER_GENERATION_FAILED', codes)
            );
          }

          return throwError(() => error);
        })
      );
  }

  private resolvePolicyNumber(
    response: PolicyNumberResponse | null | undefined
  ): string {
    const policyNumber = response?.policyNumber?.trim() ?? '';

    if (!policyNumber) {
      throw new AppError('POLICY_NUMBER_EMPTY', [
        {
          key: 'POLICY_NUMBER_NOT_FOUND',
          message: 'El servicio no retorno un numero de poliza valido.'
        }
      ]);
    }

    return policyNumber;
  }

  processUpload(
    operationType: string,
    policyNumber: string,
    selectedFile: File | null
  ): Observable<UploadResult> {
    if (!operationType?.trim()) {
      return throwError(
        () =>
          new AppError(SERVICE_ERROR_CONSTANTS.UPLOAD.FAILED, [
            SERVICE_ERROR_CONSTANTS.UPLOAD.TYPE_OPERATION_REQUIRED
          ])
      );
    }

    if (!selectedFile) {
      return throwError(
        () =>
          new AppError(SERVICE_ERROR_CONSTANTS.UPLOAD.FAILED, [
            SERVICE_ERROR_CONSTANTS.UPLOAD.FILE_INVALID
          ])
      );
    }

    const request: LoadRequest = {
      policyNumber,
      operationType
    };

    return this.fileUploadService.generateLoadId(request).pipe(
      switchMap((loadId: string) =>
        this.fileUploadService.uploadFile(selectedFile, loadId).pipe(
          map((uploadResponse) => ({ loadId, uploadResponse })),
          catchError((error: unknown) => {
            if (error instanceof AppError) {
              return throwError(
                () =>
                  Object.assign(new AppError(error.message, error.codes), {
                    generatedLoadId: loadId
                  }) as AppUploadError
              );
            }
            return throwError(() => error);
          })
        )
      )
    );
  }

  downloadTemplateBase64(
    operationType: string,
    policyNumber: string
  ): Observable<string> {
    return this.fileDownloadService.getBase64(operationType, policyNumber);
  }

  downloadFile(
    base64: string,
    fileName: string = UPLOAD_CONSTANTS.TEMPLATE_NAME
  ): void {
    this.fileDownloadService.downloadFile(base64, fileName);
  }

  uploadFile(
    selectedFile: File,
    loadId: string
  ): Observable<UploadFileResponse> {
    return this.fileUploadService.uploadFile(selectedFile, loadId);
  }

  resolveLoadId(loadId: string, uploadResponse: UploadFileResponse): string {
    return uploadResponse.loadId || loadId;
  }

  buildTechnicalIssue(error: unknown): UploadIssueDetail | null {
    if (error instanceof AppError) {
      return {
        codes: error.codes,
        fileBase64: null,
        fileName: null
      };
    }

    return null;
  }

  buildValidationIssue(uploadResponse: UploadFileResponse): UploadIssueDetail {
    return {
      codes: this.buildValidationMessages(uploadResponse),
      fileBase64: uploadResponse.annotatedFileBase64 ?? null,
      fileName: uploadResponse.annotatedFileName ?? null
    };
  }

  private buildValidationMessages(
    uploadResponse: UploadFileResponse
  ): AppErrorItem[] {
    const codes: AppErrorItem[] = [];

    if (uploadResponse.rowsWithErrors > 0 || uploadResponse.errorCount > 0) {
      codes.push({
        key: UPLOAD_CONSTANTS.VALIDATION.SUMMARY_KEY,
        message: `El archivo tiene <strong>${uploadResponse.rowsWithErrors}</strong> filas con error y en total <strong>${uploadResponse.errorCount}</strong> errores encontrados.`
      });
    }

    if (codes.length === 0) {
      codes.push({
        key: UPLOAD_CONSTANTS.VALIDATION.FAILED_KEY,
        message: UPLOAD_CONSTANTS.VALIDATION.FAILED_MESSAGE
      });
    }

    return codes;
  }
}
